import React, { useEffect, useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, Button, Avatar, Skeleton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const StatCard = ({ icon, title, value, color, loading }) => (
    <Card>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
            <Avatar sx={{ bgcolor: `${color}15`, width: 52, height: 52 }}>
                {React.cloneElement(icon, { sx: { color } })}
            </Avatar>
            <Box>
                <Typography variant="body2" color="text.secondary">{title}</Typography>
                {loading ? <Skeleton width={60} /> : <Typography variant="h5" fontWeight={700}>{value}</Typography>}
            </Box>
        </CardContent>
    </Card>
);

const DashboardPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [history, setHistory] = useState([]);
    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get('/search/history?limit=5'),
            api.get('/lists')
        ]).then(([h, l]) => {
            setHistory(h.data.data || []);
            setLists(l.data.data || []);
        }).finally(() => setLoading(false));
    }, []);

    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite';

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" fontWeight={700}>{greeting}, {user?.name?.split(' ')[0]}! 👋</Typography>
                <Typography color="text.secondary">Aqui está um resumo das suas atividades</Typography>
            </Box>

            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<SearchIcon />} title="Pesquisas realizadas" value={history.length || 0} color="#074960" loading={loading} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<ShoppingCartIcon />} title="Listas de compras" value={lists.length || 0} color="#2e7d32" loading={loading} />
                </Grid>
                {/*<Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<TrendingUpIcon />} title="Fontes integradas" value="3" color="#ed6c02" loading={false} />
                </Grid>*/}
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard icon={<HistoryIcon />} title="Pesquisas este mês" value={history.length || 0} color="#0288d1" loading={loading} />
                </Grid>
            </Grid>

            {/* Quick actions */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ background: 'linear-gradient(135deg, #074960 0%, #1a3a72 100%)', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }} onClick={() => navigate('/pesquisa')}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 52, height: 52 }}>
                                    <SearchIcon sx={{ color: '#fff', fontSize: 28 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" color="white" fontWeight={700}>Nova Pesquisa</Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.8)' }} variant="body2">Pesquisar preços em Compras.gov.br e PNCP</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }} onClick={() => navigate('/listas')}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 52, height: 52 }}>
                                    <ShoppingCartIcon sx={{ color: '#fff', fontSize: 28 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" color="white" fontWeight={700}>Nova Lista de Compras</Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.8)' }} variant="body2">Criar lista com múltiplos itens e exportar PDF</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Recent history */}
            {!loading && history.length > 0 && (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" fontWeight={600}>Pesquisas Recentes</Typography>
                            <Button size="small" onClick={() => navigate('/historico')}>Ver todas</Button>
                        </Box>
                        {history.map(h => (
                            <Box key={h._id} sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' }, cursor: 'pointer', '&:hover': { bgcolor: '#f8f9ff' }, borderRadius: 1, px: 1 }}
                                onClick={() => navigate(`/resultados?query=${encodeURIComponent(h.query)}&itemType=${h.itemType}`)}>
                                <Avatar sx={{ bgcolor: '#eef2fa', width: 36, height: 36, mr: 2 }}>
                                    <SearchIcon sx={{ color: '#074960', fontSize: 18 }} />
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" fontWeight={600}>{h.query}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {h.itemType === 'service' ? 'Serviço' : 'Material'} · {h.stats?.count || 0} cotações
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="primary" fontWeight={600}>
                                    {h.stats?.referencePrice ? `R$ ${parseFloat(h.stats.referencePrice).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                                </Typography>
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default DashboardPage;
